import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Search, Navigation, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
const competitorColors: Record<string, string> = {
  REWE: '#cc061d',
  'ALDI Nord': '#007cc1',
  'ALDI Süd': '#00457c',
  LIDL: '#0050AA'
};
const competitorLogos: Record<string, string> = {
  REWE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Rewe_Logo.png/250px-Rewe_Logo.png',
  LIDL: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/Lidl-Logo.svg/250px-Lidl-Logo.svg.png',
  'ALDI Nord': 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Aldi_Nord_201x_logo.svg/250px-Aldi_Nord_201x_logo.svg.png',
  'ALDI Süd': 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/ALDI_Sued-Logo.svg/250px-ALDI_Sued-Logo.svg.png'
};
interface TopicItem {
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
}
const competitorTopics: Array<{
  name: string;
  topics: TopicItem[];
}> = [{
  name: 'REWE',
  topics: [{
    name: 'Bio-Sortiment',
    size: 845,
    sentiment: 'positive'
  }, {
    name: 'Eigenmarken',
    size: 620,
    sentiment: 'positive'
  }, {
    name: 'Frische Produkte',
    size: 590,
    sentiment: 'positive'
  }, {
    name: 'Hohe Preise',
    size: 450,
    sentiment: 'negative'
  }, {
    name: 'Lange Wartezeiten',
    size: 380,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 310,
    sentiment: 'neutral'
  }, {
    name: 'Öffnungszeiten',
    size: 280,
    sentiment: 'neutral'
  }]
}, {
  name: 'ALDI Nord',
  topics: [{
    name: 'Günstige Preise',
    size: 920,
    sentiment: 'positive'
  }, {
    name: 'Schneller Einkauf',
    size: 750,
    sentiment: 'positive'
  }, {
    name: 'Geringe Auswahl',
    size: 610,
    sentiment: 'negative'
  }, {
    name: 'Aktionsartikel',
    size: 540,
    sentiment: 'positive'
  }, {
    name: 'Frische',
    size: 480,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 420,
    sentiment: 'neutral'
  }, {
    name: 'Unordnung',
    size: 320,
    sentiment: 'negative'
  }, {
    name: 'Standort',
    size: 310,
    sentiment: 'neutral'
  }]
}, {
  name: 'ALDI Süd',
  topics: [{
    name: 'Preis-Leistung',
    size: 880,
    sentiment: 'positive'
  }, {
    name: 'Sauberkeit',
    size: 710,
    sentiment: 'positive'
  }, {
    name: 'Backwaren',
    size: 650,
    sentiment: 'positive'
  }, {
    name: 'Beratung',
    size: 520,
    sentiment: 'negative'
  }, {
    name: 'Sortimentsbreite',
    size: 410,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 390,
    sentiment: 'neutral'
  }, {
    name: 'Kassen',
    size: 250,
    sentiment: 'neutral'
  }]
}, {
  name: 'LIDL',
  topics: [{
    name: 'Aktionsangebote',
    size: 890,
    sentiment: 'positive'
  }, {
    name: 'Effizienz',
    size: 760,
    sentiment: 'positive'
  }, {
    name: 'Lidl Plus App',
    size: 620,
    sentiment: 'positive'
  }, {
    name: 'Atmosphäre',
    size: 580,
    sentiment: 'negative'
  }, {
    name: 'Frischetheke fehlt',
    size: 490,
    sentiment: 'negative'
  }, {
    name: 'Parkplätze',
    size: 450,
    sentiment: 'neutral'
  }, {
    name: 'Wartezeiten',
    size: 350,
    sentiment: 'negative'
  }, {
    name: 'Öffnungszeiten',
    size: 320,
    sentiment: 'neutral'
  }]
}];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const radiusTopicData: Record<string, Array<{
  name: string;
  topics: TopicItem[];
}>> = {
  'Hamburg, Jungfernstieg': [{
    name: 'REWE',
    topics: [{
      name: 'Preise',
      size: 58,
      sentiment: 'negative'
    }, {
      name: 'City-Lage',
      size: 45,
      sentiment: 'positive'
    }, {
      name: 'Eng',
      size: 41,
      sentiment: 'negative'
    }, {
      name: 'Snacks',
      size: 32,
      sentiment: 'positive'
    }]
  }, {
    name: 'LIDL',
    topics: [{
      name: 'Wartezeit',
      size: 44,
      sentiment: 'negative'
    }, {
      name: 'Backshop',
      size: 38,
      sentiment: 'positive'
    }, {
      name: 'Unordentlich',
      size: 35,
      sentiment: 'negative'
    }, {
      name: 'Schnell',
      size: 29,
      sentiment: 'positive'
    }]
  }, {
    name: 'ALDI Nord',
    topics: [{
      name: 'Auswahl',
      size: 61,
      sentiment: 'negative'
    }, {
      name: 'Günstig',
      size: 52,
      sentiment: 'positive'
    }, {
      name: 'Personal',
      size: 28,
      sentiment: 'negative'
    }]
  }],
  'Berlin, Alexanderplatz': [{
    name: 'REWE',
    topics: [{
      name: 'Überfüllt',
      size: 92,
      sentiment: 'negative'
    }, {
      name: 'Öffnungszeiten',
      size: 88,
      sentiment: 'positive'
    }, {
      name: 'Preise',
      size: 65,
      sentiment: 'negative'
    }, {
      name: 'Salatbar',
      size: 45,
      sentiment: 'positive'
    }]
  }, {
    name: 'LIDL',
    topics: [{
      name: 'Sauberkeit',
      size: 81,
      sentiment: 'negative'
    }, {
      name: 'Preise',
      size: 75,
      sentiment: 'positive'
    }, {
      name: 'Kassen',
      size: 66,
      sentiment: 'negative'
    }, {
      name: 'Aktionen',
      size: 54,
      sentiment: 'positive'
    }]
  }, {
    name: 'ALDI Nord',
    topics: [{
      name: 'Sortiment',
      size: 77,
      sentiment: 'negative'
    }, {
      name: 'Wartezeit',
      size: 59,
      sentiment: 'negative'
    }, {
      name: 'Zentral',
      size: 42,
      sentiment: 'positive'
    }]
  }],
  'München, Marienplatz': [{
    name: 'REWE',
    topics: [{
      name: 'Teuer',
      size: 88,
      sentiment: 'negative'
    }, {
      name: 'Auswahl',
      size: 65,
      sentiment: 'positive'
    }, {
      name: 'Qualität',
      size: 52,
      sentiment: 'positive'
    }, {
      name: 'Kassen',
      size: 45,
      sentiment: 'negative'
    }]
  }, {
    name: 'LIDL',
    topics: [{
      name: 'Atmosphäre',
      size: 55,
      sentiment: 'negative'
    }, {
      name: 'Backwaren',
      size: 48,
      sentiment: 'positive'
    }, {
      name: 'Preise',
      size: 41,
      sentiment: 'positive'
    }, {
      name: 'Personal',
      size: 32,
      sentiment: 'negative'
    }]
  }, {
    name: 'ALDI Süd',
    topics: [{
      name: 'Parken',
      size: 71,
      sentiment: 'negative'
    }, {
      name: 'Sauberkeit',
      size: 62,
      sentiment: 'positive'
    }, {
      name: 'Frische',
      size: 55,
      sentiment: 'positive'
    }, {
      name: 'Marken',
      size: 38,
      sentiment: 'negative'
    }]
  }]
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string): number {
  if (period === 'gesamt') return value;
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
// Treemap component matching the Stimmungsbarometer layout
function SimpleTreemap({
  data
}: {
  data: TopicItem[];
}) {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-1.5 h-full" data-id="element-929">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1;
      let rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} className="rounded-lg p-2 flex flex-col justify-center items-center text-center" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-930">
            <span className="font-semibold text-xs leading-tight" style={{
          color: colors.text
        }} data-id="element-931">
              {item.name}
            </span>
            <span className="text-[10px] mt-0.5 opacity-70" style={{
          color: colors.text
        }} data-id="element-932">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
}
export function ThemenvergleichPage() {
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const handleSearch = () => {
    const key = Object.keys(radiusTopicData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  const rawLocalTopics = radiusTopicData[addressSearched] || radiusTopicData['Hamburg, Jungfernstieg'];
  // Apply time factor to data
  const currentCompetitorTopics = competitorTopics.map(comp => ({
    ...comp,
    topics: comp.topics.map(t => ({
      ...t,
      size: applyTimeFactor(t.size, selectedPeriod)
    }))
  }));
  const localTopics = rawLocalTopics.map(comp => ({
    ...comp,
    topics: comp.topics.map(t => ({
      ...t,
      size: applyTimeFactor(t.size, selectedPeriod)
    }))
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-933">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-934">
        <div data-id="element-935">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-936">Themenvergleich</h1>
          <p className="text-gray-500 mt-1" data-id="element-937">
            Stärken und Schwächen der Mitbewerber basierend auf Kundenfeedback
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white p-2 rounded-xl shadow-sm border border-gray-100" data-id="element-938">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600 pl-2" data-id="element-939">
            <Calendar size={16} data-id="element-940" />
            <span data-id="element-941">Zeitraum:</span>
          </div>
          <div className="flex gap-1" data-id="element-942">
            {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedPeriod === period.id ? 'bg-edeka-blue text-white shadow-sm' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-943">
                {period.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* 2x2 Grid for Competitor Topic Treemaps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-944">
        {currentCompetitorTopics.map((comp, index) => <motion.div key={comp.name} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} data-id="element-945">
            <Card className="h-full" data-id="element-946">
              <div className="p-6" data-id="element-947">
                <div className="flex items-center gap-3 mb-4" data-id="element-948">
                  <img src={competitorLogos[comp.name]} alt={comp.name} className="h-7 w-7 object-contain shrink-0" data-id="element-949" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-950">
                    {comp.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-951">
                  Häufig erwähnte Themen in Bewertungen
                </p>

                <div className="h-56" data-id="element-952">
                  <SimpleTreemap data={comp.topics} data-id="element-953" />
                </div>

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-954">
                  <div className="flex items-center gap-2" data-id="element-955">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#639754'
                }} data-id="element-956" />
                    <span className="text-xs text-gray-600" data-id="element-957">Positiv</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-958">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-959" />
                    <span className="text-xs text-gray-600" data-id="element-960">Neutral</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-961">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-962" />
                    <span className="text-xs text-gray-600" data-id="element-963">Negativ</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>)}
      </div>

      {/* Radius Search Card */}
      <Card data-id="element-964">
        <div className="p-6" data-id="element-965">
          <div className="mb-5" data-id="element-966">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-967">
              Lokale Themenanalyse
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-968">
              Welche Themen bewegen die Kunden im direkten Umfeld?
            </p>
          </div>

          <div className="flex gap-3 mb-6 flex-wrap" data-id="element-969">
            <div className="flex-1 min-w-48 relative" data-id="element-970">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-971" />
              <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-edeka-blue/20 focus:border-edeka-blue" data-id="element-972" />
            </div>
            <div className="flex items-center gap-2" data-id="element-973">
              <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-974">
                Radius:
              </span>
              <div className="flex gap-1" data-id="element-975">
                {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-edeka-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-976">
                    {r}
                  </button>)}
              </div>
            </div>
            <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-edeka-blue text-white rounded-lg text-sm font-medium hover:bg-edeka-blue/90 transition-colors" data-id="element-977">
              <Search size={14} data-id="element-978" />
              Suchen
            </button>
          </div>

          {hasSearched && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-979">
              <div className="text-xs text-gray-500 mb-6 flex items-center gap-1.5" data-id="element-980">
                <Navigation size={12} data-id="element-981" />
                <span data-id="element-982">
                  Ergebnisse für{' '}
                  <strong className="text-gray-700" data-id="element-983">{addressSearched}</strong>{' '}
                  im Umkreis von{' '}
                  <strong className="text-gray-700" data-id="element-984">{selectedRadius}</strong>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-985">
                {localTopics.map((comp, i) => <motion.div key={comp.name} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} className="rounded-xl border border-gray-100 bg-gray-50/50 p-4" data-id="element-986">
                    <div className="flex items-center gap-2 mb-4" data-id="element-987">
                      <img src={competitorLogos[comp.name]} alt={comp.name} className="h-5 w-5 object-contain shrink-0" data-id="element-988" />
                      <span className="font-semibold text-gray-900" data-id="element-989">
                        {comp.name}
                      </span>
                    </div>

                    <div className="h-40" data-id="element-990">
                      <SimpleTreemap data={comp.topics} data-id="element-991" />
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>}
        </div>
      </Card>
    </div>;
}